import { useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  Space,
  Table,
  Tag,
  Modal,
  Descriptions,
  Form,
  message,
  Divider,
} from "antd";
import {
  SyncOutlined,
  RedoOutlined,
  EditOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { useErrorAccounts } from "./useErrorAccounts";
import { useDebouncedCallback } from "use-debounce";
import sdk, { type GraphQLError } from "@/sdk";
import type { Moment } from "moment";
import type { MscdErrorAccount } from "@/graphql";

const { Search } = Input;
const { RangePicker } = DatePicker;

/**
 * Parse MSCD error message to find which fields are problematic.
 * e.g. "Банкны дансны дугаар /BankAccountNumber/ буруу байна"
 */
function parseErrorFields(
  errorMessage: string | null | undefined,
): Set<string> {
  if (!errorMessage) return new Set();
  const fields = new Set<string>();
  const fieldPatterns: Record<string, string[]> = {
    BankAccountNumber: ["BankAccountNumber", "Банкны дансны дугаар"],
    BankCode: ["BankCode", "Банкны код"],
    BankName: ["BankName", "Банкны нэр"],
    FirstName: ["FirstName", "Нэр"],
    LastName: ["LastName", "Овог"],
    BirthDate: ["BirthDate", "Төрсөн"],
    Gender: ["Gender", "Хүйс"],
    HomePhone: ["HomePhone", "Утас"],
    MobilePhone: ["MobilePhone", "Утас"],
    HomeAddress: ["HomeAddress", "Хаяг"],
    Occupation: ["Occupation", "Ажил"],
    RegistryNumber: ["RegistryNumber", "Регистр"],
  };
  for (const [key, patterns] of Object.entries(fieldPatterns)) {
    if (patterns.some((p) => errorMessage.includes(p))) {
      fields.add(key);
    }
  }
  return fields;
}

function DiffCell({
  mcsd,
  local,
  highlighted,
}: {
  mcsd: string | number | null | undefined;
  local: string | null | undefined;
  highlighted?: boolean;
}) {
  const mcsdStr = mcsd != null ? String(mcsd) : "-";
  const localStr = local ?? "-";
  const isDiff =
    mcsdStr !== "-" &&
    localStr !== "-" &&
    mcsdStr.toLowerCase() !== localStr.toLowerCase();
  const showWarning = isDiff || highlighted;

  return (
    <>
      <div
        style={highlighted ? { color: "red", fontWeight: "bold" } : undefined}
      >
        {mcsdStr}
        {highlighted && !isDiff && " ⚠️"}
      </div>
      <div style={{ color: showWarning ? "red" : "#999", fontSize: 12 }}>
        DB: {localStr}
        {isDiff && " ⚠️"}
      </div>
    </>
  );
}

export const MSCDErrorAccountsWidget = () => {
  const { data, loading, setBeginDate, setEndDate, refetch } =
    useErrorAccounts();

  const [search, setSearch] = useState("");
  const [selectedAccount, setSelectedAccount] =
    useState<MscdErrorAccount | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();
  const [linkCandidate, setLinkCandidate] = useState<{
    userId: string;
    firstName?: string | null;
    lastName?: string | null;
    phoneNo?: string | null;
    email?: string | null;
    matchedBy: string;
    existingAccountNo?: string | null;
  } | null>(null);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value);
  }, 300);

  const handleDateChange = (values: [Moment | null, Moment | null] | null) => {
    if (values?.[0] && values?.[1]) {
      setBeginDate(values[0].format("YYYYMMDD"));
      setEndDate(values[1].format("YYYYMMDD"));
    }
  };

  const retryMutation = useMutation({
    mutationFn: (userId: string) => sdk.retryMSCDAccountCreation({ userId }),
    onSuccess: () => {
      message.success("Дахин илгээлт амжилттай");
      setSelectedAccount(null);
      refetch();
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа",
      );
    },
  });

  const fixAndRetryMutation = useMutation({
    mutationFn: (params: {
      userId: string;
      bankAccountNo?: string;
      bankCode?: string;
      bankName?: string;
      firstName?: string;
      lastName?: string;
      phoneNo?: string;
      birthDate?: string;
    }) => sdk.fixAndRetryMSCDAccount(params),
    onSuccess: () => {
      message.success("Мэдээлэл засагдаж, дахин илгээгдлээ");
      setSelectedAccount(null);
      setEditMode(false);
      refetch();
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа",
      );
    },
  });

  const searchLinkMutation = useMutation({
    mutationFn: (params: {
      bdcAccountId: string;
      bdcAccountNumber: number;
      registerNo?: string;
      phoneNo?: string;
    }) => sdk.searchUserForMSCDLink(params),
    onSuccess: (data) => {
      const candidate = data.searchUserForMSCDLink;
      if (!candidate) {
        message.warning("Тохирох хэрэглэгч олдсонгүй");
        return;
      }
      setLinkCandidate(candidate);
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа",
      );
    },
  });

  const linkMutation = useMutation({
    mutationFn: (params: {
      bdcAccountId: string;
      bdcAccountNumber: number;
      userId: string;
    }) => sdk.linkMSCDErrorAccount(params),
    onSuccess: async () => {
      message.success("DB холбогдлоо");
      setLinkCandidate(null);
      const result = await refetch();
      // Refresh selectedAccount with new data
      if (selectedAccount && result.data) {
        const updated = result.data.find(
          (a) => a.PkId === selectedAccount.PkId,
        );
        if (updated) setSelectedAccount(updated);
      }
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа",
      );
    },
  });

  const handleFixAndRetry = () => {
    if (!selectedAccount?.localUserId) return;
    const values = form.getFieldsValue();

    // Only send changed fields
    const params: {
      userId: string;
      bankAccountNo?: string;
      bankCode?: string;
      bankName?: string;
      firstName?: string;
      lastName?: string;
      phoneNo?: string;
      birthDate?: string;
    } = { userId: selectedAccount.localUserId };
    if (values.bankAccountNo !== selectedAccount.localBankAccountNo)
      params.bankAccountNo = values.bankAccountNo;
    if (values.bankCode !== selectedAccount.localBankCode)
      params.bankCode = values.bankCode;
    if (values.bankName !== selectedAccount.localBankName)
      params.bankName = values.bankName;
    if (values.firstName !== selectedAccount.localFirstName)
      params.firstName = values.firstName;
    if (values.lastName !== selectedAccount.localLastName)
      params.lastName = values.lastName;
    if (values.phoneNo !== selectedAccount.localPhoneNo)
      params.phoneNo = values.phoneNo;
    if (values.birthDate !== (selectedAccount.localBirthDate ?? ""))
      params.birthDate = values.birthDate;

    fixAndRetryMutation.mutate(params);
  };

  const openEditMode = () => {
    if (!selectedAccount) return;
    form.setFieldsValue({
      bankAccountNo: selectedAccount.localBankAccountNo ?? "",
      bankCode: selectedAccount.localBankCode ?? "",
      bankName: selectedAccount.localBankName ?? "",
      firstName: selectedAccount.localFirstName ?? "",
      lastName: selectedAccount.localLastName ?? "",
      phoneNo: selectedAccount.localPhoneNo ?? "",
      birthDate: selectedAccount.localBirthDate ?? "",
    });
    setEditMode(true);
  };

  const filtered = search
    ? data.filter((acc) => {
        const s = search.toLowerCase();
        return (
          acc.FirstName?.toLowerCase().includes(s) ||
          acc.LastName?.toLowerCase().includes(s) ||
          acc.RegistryNumber?.toLowerCase().includes(s) ||
          acc.BDCAccountId?.toLowerCase().includes(s) ||
          acc.ErrorMessage?.toLowerCase().includes(s)
        );
      })
    : data;

  const acc = selectedAccount;

  return (
    <>
      <div className="page-header">
        <h3>Алдаатай данс ({filtered.length})</h3>
        <Space>
          <Search
            placeholder="Хайх..."
            onChange={(e) => debouncedSearch(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <RangePicker
            onChange={(values) => handleDateChange(values)}
            placeholder={["Эхлэх огноо", "Дуусах огноо"]}
          />
          <Button
            type="primary"
            icon={<SyncOutlined />}
            loading={loading}
            onClick={() => refetch()}
          >
            Шинэчлэх
          </Button>
        </Space>
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table
            scroll={{ x: 1400 }}
            dataSource={filtered}
            size="small"
            loading={loading}
            rowKey="PkId"
            bordered
            onRow={(record) => ({
              onClick: () => {
                setSelectedAccount(record);
                setEditMode(false);
              },
              style: { cursor: "pointer" },
            })}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total) => `Нийт: ${total}`,
            }}
            columns={[
              {
                title: "BDC Account ID",
                dataIndex: "BDCAccountId",
                key: "BDCAccountId",
                width: 160,
              },
              {
                title: "Овог",
                dataIndex: "LastName",
                key: "LastName",
              },
              {
                title: "Нэр",
                dataIndex: "FirstName",
                key: "FirstName",
              },
              {
                title: "РД",
                dataIndex: "RegistryNumber",
                key: "RegistryNumber",
                width: 120,
              },
              {
                title: "Статус",
                dataIndex: "Status",
                key: "Status",
                width: 80,
                render: (status: number) => (
                  <Tag
                    color={
                      status === 2
                        ? "red"
                        : status === 21
                          ? "orange"
                          : "default"
                    }
                  >
                    {status === 2
                      ? "Алдаа"
                      : status === 21
                        ? "Хүлээгдэж"
                        : status}
                  </Tag>
                ),
              },
              {
                title: "Алдааны мэдээлэл",
                dataIndex: "ErrorMessage",
                key: "ErrorMessage",
                ellipsis: true,
                width: 300,
              },
              {
                title: "DB",
                key: "matched",
                width: 50,
                fixed: "right",
                render: (_: unknown, record: MscdErrorAccount) =>
                  record.localUserId ? (
                    <Tag color="green">✓</Tag>
                  ) : (
                    <Tag color="red">✗</Tag>
                  ),
              },
            ]}
          />
        </div>
      </div>

      <Modal
        title="Алдаатай дансны дэлгэрэнгүй"
        open={!!acc}
        onCancel={() => {
          setSelectedAccount(null);
          setEditMode(false);
        }}
        width={800}
        footer={
          acc?.localUserId
            ? editMode
              ? [
                  <Button key="cancel" onClick={() => setEditMode(false)}>
                    Болих
                  </Button>,
                  <Button
                    key="save"
                    type="primary"
                    icon={<RedoOutlined />}
                    loading={fixAndRetryMutation.isLoading}
                    onClick={handleFixAndRetry}
                  >
                    Хадгалж дахин илгээх
                  </Button>,
                ]
              : [
                  <Button key="close" onClick={() => setSelectedAccount(null)}>
                    Хаах
                  </Button>,
                  <Button
                    key="edit"
                    icon={<EditOutlined />}
                    onClick={openEditMode}
                  >
                    Засах
                  </Button>,
                  <Button
                    key="retry"
                    type="primary"
                    icon={<RedoOutlined />}
                    loading={retryMutation.isLoading}
                    onClick={() => retryMutation.mutate(acc.localUserId!)}
                  >
                    Дахин илгээх
                  </Button>,
                ]
            : acc
              ? [
                  <Button
                    key="close"
                    onClick={() => {
                      setSelectedAccount(null);
                      setLinkCandidate(null);
                    }}
                  >
                    Хаах
                  </Button>,
                  !linkCandidate ? (
                    <Button
                      key="search"
                      type="primary"
                      icon={<LinkOutlined />}
                      loading={searchLinkMutation.isLoading}
                      onClick={() =>
                        searchLinkMutation.mutate({
                          bdcAccountId: acc.BDCAccountId,
                          bdcAccountNumber: acc.BDCAccountNumber,
                          registerNo: acc.RegistryNumber || undefined,
                          phoneNo:
                            acc.MobilePhone || acc.HomePhone || undefined,
                        })
                      }
                    >
                      Хэрэглэгч хайх
                    </Button>
                  ) : (
                    <Button
                      key="link"
                      type="primary"
                      icon={<LinkOutlined />}
                      loading={linkMutation.isLoading}
                      onClick={() =>
                        linkMutation.mutate({
                          bdcAccountId: acc.BDCAccountId,
                          bdcAccountNumber: acc.BDCAccountNumber,
                          userId: linkCandidate.userId,
                        })
                      }
                    >
                      Холбох
                    </Button>
                  ),
                ]
              : null
        }
      >
        {acc && (
          <>
            <Tag
              color={acc.Status === 2 ? "red" : "orange"}
              style={{ marginBottom: 16, fontSize: 14 }}
            >
              {acc.ErrorMessage}
            </Tag>

            {editMode ? (
              <>
                <Divider orientation="left">Мэдээлэл засах</Divider>
                <Form form={form} layout="vertical" size="small">
                  <Form.Item label="Овог" name="lastName">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Нэр" name="firstName">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Утас" name="phoneNo">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Банкны код" name="bankCode">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Банкны нэр" name="bankName">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Банкны дансны дугаар" name="bankAccountNo">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Төрсөн огноо (YYYY-MM-DD)" name="birthDate">
                    <Input placeholder="1990-01-15" />
                  </Form.Item>
                </Form>
              </>
            ) : (
              (() => {
                const ef = parseErrorFields(acc.ErrorMessage);
                return (
                  <Descriptions
                    bordered
                    column={2}
                    size="small"
                    title="MCSD vs Манай DB"
                  >
                    <Descriptions.Item label="BDC Account ID">
                      <DiffCell
                        mcsd={acc.BDCAccountId}
                        local={acc.localAccountNo}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="BDC Account Number">
                      {acc.BDCAccountNumber}
                    </Descriptions.Item>
                    <Descriptions.Item label="Овог">
                      <DiffCell
                        mcsd={acc.LastName}
                        local={acc.localLastName}
                        highlighted={ef.has("LastName")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Нэр">
                      <DiffCell
                        mcsd={acc.FirstName}
                        local={acc.localFirstName}
                        highlighted={ef.has("FirstName")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Регистрийн дугаар">
                      <DiffCell
                        mcsd={acc.RegistryNumber}
                        local={acc.localRegisterNo}
                        highlighted={ef.has("RegistryNumber")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Төрсөн огноо">
                      <DiffCell
                        mcsd={acc.BirthDate?.split("T")[0]}
                        local={acc.localBirthDate}
                        highlighted={ef.has("BirthDate")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Хүйс">
                      <DiffCell
                        mcsd={acc.Gender}
                        local={acc.localGender}
                        highlighted={ef.has("Gender")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Утас">
                      <DiffCell
                        mcsd={acc.MobilePhone}
                        local={acc.localPhoneNo}
                        highlighted={
                          ef.has("MobilePhone") || ef.has("HomePhone")
                        }
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Ажил мэргэжил">
                      <DiffCell
                        mcsd={acc.Occupation}
                        local={acc.localOccupation}
                        highlighted={ef.has("Occupation")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Хаяг" span={2}>
                      <DiffCell
                        mcsd={acc.HomeAddress}
                        local={acc.localHomeAddress}
                        highlighted={ef.has("HomeAddress")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Банкны код">
                      <DiffCell
                        mcsd={acc.BankCode}
                        local={acc.localBankCode}
                        highlighted={ef.has("BankCode")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Банк">
                      <DiffCell
                        mcsd={acc.BankName}
                        local={acc.localBankName}
                        highlighted={ef.has("BankName")}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Банкны данс" span={2}>
                      <DiffCell
                        mcsd={acc.BankAccountNumber}
                        local={acc.localBankAccountNo}
                        highlighted={ef.has("BankAccountNumber")}
                      />
                    </Descriptions.Item>
                  </Descriptions>
                );
              })()
            )}

            {!acc.localUserId && !linkCandidate && (
              <div style={{ marginTop: 16, color: "#999" }}>
                Манай DB дотор тохирох хэрэглэгч олдсонгүй (BDCAccountId:{" "}
                {acc.BDCAccountId}, BDCAccountNumber: {acc.BDCAccountNumber})
              </div>
            )}

            {linkCandidate && (
              <div style={{ marginTop: 16 }}>
                <Divider orientation="left">Олдсон хэрэглэгч</Divider>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Овог">
                    {linkCandidate.lastName ?? "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Нэр">
                    {linkCandidate.firstName ?? "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Утас">
                    {linkCandidate.phoneNo ?? "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Имэйл">
                    {linkCandidate.email ?? "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Олдсон арга" span={2}>
                    <Tag
                      color={
                        linkCandidate.matchedBy === "bdcAccountId" ||
                        linkCandidate.matchedBy === "bdcAccountNumber"
                          ? "green"
                          : "blue"
                      }
                    >
                      {linkCandidate.matchedBy === "bdcAccountId"
                        ? "BDC Account ID"
                        : linkCandidate.matchedBy === "bdcAccountNumber"
                          ? "BDC Account Number"
                          : linkCandidate.matchedBy === "phoneNo"
                            ? "Утасны дугаар"
                            : "Регистрийн дугаар"}
                    </Tag>
                  </Descriptions.Item>
                  {linkCandidate.existingAccountNo && (
                    <Descriptions.Item label="Одоогийн дансны дугаар" span={2}>
                      {linkCandidate.existingAccountNo}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};
